import React, { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'
import { sectionDataConstant } from '../core/_constants'
import { ToolTipUI } from '../../widgets/components/UI/ToolTipUI'
import { EditorComp } from './EditorComp'
import { DragDropFile } from './DragDrop'
import { toast } from 'react-toastify'
import { KpiComponent } from './KpiComponent'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { useInvestorDatabase } from '../../investor-database/core/InvestorContext'
import { useAuth } from '../../auth'
import {
  createCopyInvestorUpdateAPI,
  createInvestorUpdateAPI,
  deleteInvestorUpdateAPI,
  editInvestorUpdatesAPI,
  getSingleInvestorUpdatesAPI,
} from '../core/_request'
import { useNavigate, useParams } from 'react-router-dom'
import { DeleteConfirmationModal } from '../views/DeleteConfirmationModal'
import { CopyConfirmationModal } from '../views/CopyConfirmationModal'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { DateTime } from 'luxon'
import { DropdownType } from '../../../core/_models'
import { currencies } from '../../onboarding/core/_requests'
import { errorMessage } from '../../profile/core/_constants'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../hooks/gtmHelpers'
import { useThemeMode } from '../../../../_metronic/partials'

const CreateUpdates = () => {
  const { formatMessage } = useIntl()
  const { mode } = useThemeMode()
  const { currentUser, companyId, setShowBillingModal } = useAuth()
  const [randomValue, setRandomValue] = useState<string>()
  const [updateType, setUpdateType] = useState<string>('INTERNAL')
  const [useCompanyLogo, setUseCompanyLogo] = useState<boolean>(true)
  const [showAuthor, setShowAuthor] = useState<boolean>(true)
  const [infoInput, setInfoInput] = useState<string>()
  const [infoDate, setInfoDate] = useState<string>()
  const [inputError, setInputError] = useState<string>()
  const [viewTracking, setViewTracking] = useState<boolean>(false)

  const [showDeleteModal, setDeleteShowModal] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [showMakeACopyModal, setMakeACopyShowModal] = useState(false)
  const [makeACopyLoading, setMakeACopyLoading] = useState(false)

  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)

  const navigate = useNavigate()

  const [sectionData, setSectionData] = useState<any[]>(sectionDataConstant)
  const [currencyOptions, setCurrencyOptions] = useState<any[]>([])

  const {
    willRun,
    setWillRun,
    willEditRun,
    setWillEditRun,
    willDeleteRun,
    setWillDeleteRun,
    setInvestorUpdateLoading,
    investorUpdate,
    setInvestorUpdate,
    willCopyRun,
    setWillCopyRun,
    isEditing,
    setIsEditing,
    setInvestorUpdatePLoading,
  } = useInvestorDatabase()

  const inputRef = useRef<any>(null)
  const { updateId } = useParams()

  useEffect(() => {
    let currencyData: DropdownType[] = []
    const getCurrencies = async () => {
      try {
        const {
          data: { data: currency, success },
        } = await currencies()
        if (success) {
          currencyData = currency.map((curr: any) => {
            return {
              id: curr.code,
              name: `${curr.code} (${curr.symbol}) - ${curr.currency}`,
              value: curr.symbol,
            }
          })
          setCurrencyOptions([...currencyData])
        }
      } catch (err) {
        console.log(err)
      }
    }
    getCurrencies()
  }, [])

  const getSingleInvestorUpdateData = async (updateId: number) => {
    const {
      data: { data: singleInvestorUpdate, success },
    } = await getSingleInvestorUpdatesAPI(+updateId!)
    if (success) {
      setSectionData(JSON.parse(singleInvestorUpdate?.content))
      setViewTracking(singleInvestorUpdate?.trackView)
      if (singleInvestorUpdate?.timeframe) {
        setStartDate(
          DateTime.fromFormat(
            JSON.parse(singleInvestorUpdate?.timeframe)[0],
            'yyyy-MM-dd HH:mm:ss'
          ).toJSDate()
        )
        setEndDate(
          DateTime.fromFormat(
            JSON.parse(singleInvestorUpdate?.timeframe)[1],
            'yyyy-MM-dd HH:mm:ss'
          ).toJSDate()
        )
      }

      setShowAuthor(singleInvestorUpdate?.showAuthor)
      setInfoInput(singleInvestorUpdate?.title)
      setUseCompanyLogo(singleInvestorUpdate?.showLogo)
      setUpdateType(singleInvestorUpdate?.type)
      setInvestorUpdate({ ...singleInvestorUpdate })
    }
  }

  useEffect(() => {
    randomValuefn()
    setInvestorUpdate(undefined)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateId) {
      getSingleInvestorUpdateData(+updateId)
      setIsEditing(true)
    } else {
      setIsEditing(false)
    }
  }, [updateId]) // eslint-disable-line react-hooks/exhaustive-deps

  const onUpdateSubmit = async () => {
    console.log(currencyOptions)
    try {
      if (!infoInput) {
        setInputError(formatMessage({ id: 'Title is required' }))
        inputRef?.current?.focus()
        return
      } else if (infoInput.length >= 1 && infoInput.length < 3) {
        return
      } else {
        if (companyId) {
          setInvestorUpdateLoading(true)
          const {
            data: { data: createdData, success, errors },
          } = await createInvestorUpdateAPI(companyId, {
            title: infoInput,
            timeframe: infoDate,
            type: updateType,
            showLogo: useCompanyLogo,
            showAuthor: showAuthor,
            trackView: viewTracking,
            content: JSON.stringify(sectionData),
          })
          if (success) {
            navigate(`/updates/create-update/${createdData.updateId}`)
            toast.success(formatMessage({ id: 'Investor Update created successfully' }))
            TagManager.dataLayer({
              dataLayer: {
                event: GTMEvent.CreateInvestorUpdateSuccessful,
                action: 'normal',
                metadata: {
                  title: infoInput,
                  timeframe: infoDate,
                  type: updateType,
                  show_logo: useCompanyLogo,
                  show_author: showAuthor,
                  track_view: viewTracking,
                  user_id: currentUser?.userId,
                  email: currentUser?.email,
                  company_id: companyId,
                },
              },
            })
          } else {
            errors.forEach((error: string) => {
              if (
                error === errorMessage.UPGRADE_BILLING_PLAN_LIMIT_EXCEEDED ||
                error === errorMessage.UPGRADE_BILLING_PLAN_FEATURE
              ) {
                setShowBillingModal(true)
                toast.error(formatMessage({ id: error }), { autoClose: false })
              } else {
                toast.error(formatMessage({ id: error }))
              }
            })
            TagManager.dataLayer({
              dataLayer: {
                event: GTMEvent.CreateInvestorUpdateFailed,
                action: 'normal',
                metadata: {
                  title: infoInput,
                  timeframe: infoDate,
                  type: updateType,
                  show_logo: useCompanyLogo,
                  show_author: showAuthor,
                  track_view: viewTracking,
                  user_id: currentUser?.userId,
                  email: currentUser?.email,
                  company_id: companyId,
                  error: errors[0],
                },
              },
            })
          }
        }
      }
    } catch (err) {
      console.log(err)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.CreateInvestorUpdateFailed,
          action: 'normal',
          metadata: {
            title: infoInput,
            timeframe: infoDate,
            type: updateType,
            show_logo: useCompanyLogo,
            show_author: showAuthor,
            track_view: viewTracking,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            company_id: companyId,
            error: err,
          },
        },
      })
    } finally {
      setInvestorUpdateLoading(false)
    }
  }
  const onEditPInvestorUpdate = async () => {
    try {
      setInvestorUpdatePLoading(true)
      const statusValue = investorUpdate?.status === 'DRAFT' ? 'PUBLISHED' : 'DRAFT'
      const {
        data: { data: editedData, success },
      } = await editInvestorUpdatesAPI(investorUpdate?.updateId!, { status: statusValue })
      if (success) {
        setInvestorUpdate({ ...editedData })
        toast.success(formatMessage({ id: `Successfully update to ${editedData?.status}` }))
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.EditInvestorUpdatesStatusSuccessful,
            action: 'normal',
            metadata: {
              user_id: currentUser?.userId,
              email: currentUser?.email,
              company_id: companyId,
              investor_update_id: investorUpdate?.updateId!,
              status: investorUpdate?.status === 'DRAFT' ? 'PUBLISHED' : 'DRAFT',
            },
          },
        })
      }
    } catch (err) {
      console.log(err)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.EditInvestorUpdatesStatusFailed,
          action: 'normal',
          metadata: {
            user_id: currentUser?.userId,
            email: currentUser?.email,
            company_id: companyId,
            investor_update_id: investorUpdate?.updateId!,
            status: investorUpdate?.status === 'DRAFT' ? 'PUBLISHED' : 'DRAFT',
            error: err,
          },
        },
      })
    } finally {
      setInvestorUpdatePLoading(false)
    }
  }
  const onEditInvestorUpdate = async () => {
    try {
      setInvestorUpdateLoading(true)
      const editData = {
        title: infoInput,
        timeframe: infoDate,
        type: updateType,
        showLogo: useCompanyLogo,
        showAuthor: showAuthor,
        trackView: viewTracking,
        content: JSON.stringify(sectionData),
      }
      const {
        data: { data: editedData, success },
      } = await editInvestorUpdatesAPI(investorUpdate?.updateId!, editData)
      if (success) {
        setInvestorUpdate({ ...editedData })
        toast.success(formatMessage({ id: `Successfully saved the Investor update` }))
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.EditInvestorUpdatesSuccessful,
            action: 'normal',
            metadata: {
              user_id: currentUser?.userId,
              email: currentUser?.email,
              company_id: companyId,
              investor_update_id: investorUpdate?.updateId!,
              title: infoInput,
              timeframe: infoDate,
              type: updateType,
              show_logo: useCompanyLogo,
              show_author: showAuthor,
              track_view: viewTracking,
            },
          },
        })
      }
    } catch (err) {
      console.log(err)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.EditInvestorUpdatesFailed,
          action: 'normal',
          metadata: {
            user_id: currentUser?.userId,
            email: currentUser?.email,
            company_id: companyId,
            investor_update_id: investorUpdate?.updateId!,
            title: infoInput,
            timeframe: infoDate,
            type: updateType,
            show_logo: useCompanyLogo,
            show_author: showAuthor,
            track_view: viewTracking,
            error: err,
          },
        },
      })
    } finally {
      setInvestorUpdateLoading(false)
    }
  }

  const onDeleteSubmit = async () => {
    try {
      setDeleteLoading(true)
      const {
        data: { success },
      }: any = await deleteInvestorUpdateAPI(investorUpdate?.updateId)
      if (success) {
        setInvestorUpdate(undefined)
        setDeleteShowModal(false)
        navigate(`/updates/update`)
        toast.success(formatMessage({ id: 'Successfully deleted investor update' }))
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.DeleteInvestorUpdatesSuccessful,
            action: 'normal',
            metadata: {
              user_id: currentUser?.userId,
              email: currentUser?.email,
              company_id: companyId,
              investor_update_id: investorUpdate?.updateId!,
            },
          },
        })
      }
    } catch (err) {
      console.log(err)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.DeleteInvestorUpdatesFailed,
          action: 'normal',
          metadata: {
            user_id: currentUser?.userId,
            email: currentUser?.email,
            company_id: companyId,
            investor_update_id: investorUpdate?.updateId!,
            error: err,
          },
        },
      })
    } finally {
      setDeleteLoading(false)
    }
  }
  const onCopySubmit = async () => {
    try {
      setMakeACopyLoading(true)
      const {
        data: { success, errors },
      } = await createCopyInvestorUpdateAPI(investorUpdate?.updateId)
      if (success) {
        setInvestorUpdate(undefined)
        setMakeACopyShowModal(false)
        toast.success(formatMessage({ id: 'Investor Update copied successfully' }))
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.CreateCopyInvestorUpdatesSuccessful,
            action: 'normal',
            metadata: {
              user_id: currentUser?.userId,
              email: currentUser?.email,
              company_id: companyId,
              investor_update_id: investorUpdate?.updateId!,
            },
          },
        })
        navigate(`/updates/update`)
      } else {
        errors.forEach((error: string) => {
          toast.error(formatMessage({ id: error }))
        })
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.CreateCopyInvestorUpdatesFailed,
            action: 'normal',
            metadata: {
              user_id: currentUser?.userId,
              email: currentUser?.email,
              company_id: companyId,
              investor_update_id: investorUpdate?.updateId!,
              error: errors[0],
            },
          },
        })
      }
    } catch (err) {
      console.log(err)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.CreateCopyInvestorUpdatesFailed,
          action: 'normal',
          metadata: {
            user_id: currentUser?.userId,
            email: currentUser?.email,
            company_id: companyId,
            investor_update_id: investorUpdate?.updateId!,
            error: err,
          },
        },
      })
    } finally {
      setMakeACopyLoading(false)
    }
  }
  useEffect(() => {
    if (willRun && !investorUpdate?.updateId) {
      onUpdateSubmit()
      setWillRun(false)
    }
    if (willRun && investorUpdate?.updateId) {
      onEditPInvestorUpdate()
      setWillRun(false)
    }
  }, [willRun, investorUpdate]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (willEditRun && isEditing) {
      onEditInvestorUpdate()
      setWillEditRun(false)
    }
  }, [willEditRun, investorUpdate]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (willDeleteRun && investorUpdate) {
      setDeleteShowModal(true)
      setWillDeleteRun(false)
    }
  }, [willDeleteRun, investorUpdate]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (willCopyRun && investorUpdate) {
      setMakeACopyShowModal(true)
      setWillCopyRun(false)
    }
  }, [willCopyRun, investorUpdate]) // eslint-disable-line react-hooks/exhaustive-deps

  //Saving file Id to send its value to backend
  const onFileUploadCompleted = (id: number, name: string, index: number) => {
    sectionData[index].ids.push({ id, name })
    setSectionData([...sectionData])
  }

  // To find randon update_values
  const randomValuefn = () => {
    const randomNumber = new Date().getTime()
    const date = DateTime.fromMillis(randomNumber)
    const formattedDate = date.toFormat('yyyy/MM/dd')
    setRandomValue(`Update_${formattedDate}`)
  }

  const onInputTextValueChange = (inputText: string, index: number) => {
    sectionData[index].inputText = inputText
    setSectionData([...sectionData])
  }

  // Using this function to update/store rich text data.
  const onRichTextValueChange = (richtextValue: string, index: number) => {
    sectionData[index].richText = richtextValue
    setSectionData([...sectionData])
  }
  // KPI values Start
  // will use to update toogle , text and drop-down of kpi.
  const handleToogle = (id: number, what: string, checked: boolean | string, index: number) => {
    const kpi = sectionData.at(index!)
    const newKpiValues = kpi?.kpiValues?.map((kpiValue: any) => {
      if (kpiValue.id === id) {
        if (what === 'toogle') {
          if (checked) {
            return { ...kpiValue, toogle: checked, value: '0' }
          } else {
            return { ...kpiValue, value: '', toogle: checked }
          }
        } else if (what === 'title') {
          return { ...kpiValue, title: checked }
        } else if (what === 'value') {
          if (typeof checked === 'string' && checked.length > 0) {
            return { ...kpiValue, value: checked, toogle: true }
          } else {
            return { ...kpiValue, value: checked, toogle: false }
          }
        } else {
          return { ...kpiValue, type: checked }
        }
      }
      return kpiValue
    })
    kpi.kpiValues = newKpiValues
    sectionData[index] = kpi
    setSectionData([...sectionData])
  }

  // to add new kpi row , when user click on add new kpi
  const addNewKpi = (id: number, index: number) => {
    sectionData[index].kpiValues.push({
      id: id,
      toogle: false,
      title: 'New KPI',
      value: '',
      type: 'Currency',
    })
    setSectionData([...sectionData])
  }

  // to delete kpi row , when user click on delete
  const deleteKpi = (id: number, index: number) => {
    const fitleredData = sectionData[index].kpiValues?.filter((kpi: { id: number }) => kpi.id !== id)
    sectionData[index].kpiValues = fitleredData
    setSectionData([...sectionData])
  }

  // to drag data inside a kpi section.
  const onDragEnd = (result: any, index: number) => {
    if (!result.destination) {
      return
    }
    const [reorderedItem] = sectionData[index].kpiValues.splice(result.source.index, 1)
    sectionData[index].kpiValues.splice(result.destination.index, 0, reorderedItem)
    setSectionData([...sectionData])
  }
  // KPI values End

  // to delete whole "section"
  const onDeleteWholeSection = (index: number) => {
    sectionData.splice(index, 1)
    setSectionData([...sectionData])
  }

  //Main drag between source and destination
  const OnDragEndMain = (result: any) => {
    if (!result.destination) {
      return
    }

    if (
      result.source.droppableId === 'main_drag_section' &&
      result.destination.droppableId === 'main_drop_section'
    ) {
      if (result.draggableId === 'richText') {
        sectionData.splice(result.destination.index, 0, {
          type: 'richText',
          inputText: '',
          richText: '',
        })
        setSectionData([...sectionData])
      } else if (result.draggableId === 'kpiValues') {
        sectionData.splice(result.destination.index, 0, {
          type: 'kpiValues',
          currencyType: '$',
          kpiValues: [],
        })
        setSectionData([...sectionData])
      } else if (result.draggableId === 'documents') {
        sectionData.splice(result.destination.index, 0, {
          type: 'documents',
          inputText: '',
          ids: [],
        })
        setSectionData([...sectionData])
      } else {
        sectionData.splice(result.destination.index, 0, {
          type: 'lineBreak',
        })
        setSectionData([...sectionData])
      }
    }

    if (
      result.source.droppableId === 'main_drop_section' &&
      result.destination.droppableId === 'main_drop_section'
    ) {
      const [reorderedItem] = sectionData.splice(result.source.index, 1)
      sectionData.splice(result.destination.index, 0, reorderedItem)
      setSectionData([...sectionData])
    }
  }

  /*
  const kpiCurrencyChange = (value: string, index: number) => {
    sectionData[index].currencyType = value
    setSectionData([...sectionData])
  }*/

  return (
    <>
      <div className={`mt-2 create-updates ${mode === 'dark' ? 'updates-border-dark' : 'updates-border '}`}>
        <DragDropContext onDragEnd={OnDragEndMain}>
          <div className='mt-4 d-flex gap-5 mb-10'>
            <Droppable droppableId='main_drag_section'>
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  <div className='d-flex flex-column gap-5 w-300px drag-drop-section'>
                    <div>
                      <span className='font-size-14 font-weight-600'>
                        {formatMessage({ id: 'Drag and drop sections' })}
                      </span>
                    </div>

                    <Draggable key={1} draggableId={'documents'} index={1}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <div className='bg-white border-radius-box p-5'>
                            <div className='d-flex align-items-center mb-2'>
                              <img
                                src={toAbsoluteUrl('/media/icons/investor-updates/threeArrow.svg')}
                                alt=''
                              />
                              <p className='ps-2 m-0 font-weight-400 font-size-12 text-color'>
                                {formatMessage({
                                  id: 'Attach files/ documents and images (e.g pdf, csv etc)',
                                })}
                              </p>
                            </div>
                            <div className='box-height d-flex justify-content-center align-items-center mx-3 mb-2'>
                              <img
                                src={toAbsoluteUrl(
                                  '/media/icons/investor-updates/attached_file.svg'
                                )}
                                alt=''
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                    <Draggable key={2} draggableId={'richText'} index={2}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <div>
                            <EditorComp is_drag_source={true} />
                          </div>
                        </div>
                      )}
                    </Draggable>
                    <Draggable key={3} draggableId={'lineBreak'} index={3}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <div className='bg-white border-radius-box p-5'>
                            <div className='d-flex align-items-center mb-2'>
                              <img
                                src={toAbsoluteUrl('/media/icons/investor-updates/threeArrow.svg')}
                                alt=''
                              />

                              <p className='ps-2 m-0 font-weight-400 font-size-12 text-color'>
                                {formatMessage({ id: 'Add Seperator Line' })}
                              </p>
                            </div>
                            <div className='border border-bottom-2 mx-5 mt-5'></div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                    <Draggable key={4} draggableId={'kpiValues'} index={4}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <div className='bg-white border-radius-box p-5'>
                            <div className='d-flex align-items-center mb-2'>
                              <img
                                src={toAbsoluteUrl('/media/icons/investor-updates/threeArrow.svg')}
                                alt=''
                              />
                              <p className='m-0 ps-2 font-weight-400 font-size-12 text-color'>
                                {formatMessage({ id: 'Add KPIs' })}
                              </p>
                            </div>
                            <div>
                              <p className='ps-2 m-0 font-weight-400 font-size-12'>
                                {formatMessage({
                                  id: 'Add measurable value that demonstrates how effectively a company is achieving key business objectives',
                                })}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  </div>
                  {provided.placeholder}
                </div>
              )}
            </Droppable>

            <div className='w-100 me-10 template-section'>
              <div className='mb-5'>
                <span className='font-size-14 font-weight-600'>
                  {formatMessage({ id: 'Template Structure' })}
                </span>
              </div>
              <div className=' p-5 template-structure gap-8 '>
                {/* Radio button */}
                <div className='d-flex text-white'>
                  <div className='d-flex'>
                    <div>
                      <input
                        type='radio'
                        id='radio'
                        name='radio-button'
                        value='INTERNAL'
                        checked={updateType === 'INTERNAL'}
                        onChange={(event) => setUpdateType(event.target.value)}
                      />
                    </div>
                    <div>
                      <label className={`text-dark ps-2 ${updateType !== 'INTERNAL' && 'text-muted'}`}>
                        {formatMessage({ id: 'Internal Update' })}
                      </label>
                    </div>
                  </div>
                  <div className='d-flex ms-4'>
                    <div>
                      <input
                        type='radio'
                        id='radio'
                        name='radio-button'
                        value='EXTERNAL'
                        checked={updateType === 'EXTERNAL'}
                        onChange={(event) => setUpdateType(event.target.value)}
                      />
                    </div>
                    <div>
                      <label className={`text-dark ps-2 ${updateType !== 'EXTERNAL' && 'text-muted'}`}>
                        {formatMessage({ id: 'External Update' })}
                      </label>
                    </div>
                  </div>
                </div>
                {/* checkbox */}
                <div className='d-flex text-white kpi-input'>
                  <div className='form-check form-switch  form-check-solid '>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='flexSwitchCheckDefault'
                      checked={useCompanyLogo}
                      onChange={(event) => setUseCompanyLogo(event.target.checked)}
                    />
                    <label className='form-check-label text-white font-size-13 font-weight-400'>
                      {formatMessage({ id: 'Use company Logo' })}
                    </label>
                    <p className='switch-text-color pt-1 font-size-12 font-weight-400'>
                      {formatMessage({ id: 'company logo on heading of the file' })}
                    </p>
                  </div>

                  <div className='ps-4'>
                    <div className='form-check form-switch  form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='flexSwitchCheckDefault'
                        checked={showAuthor}
                        onChange={(event) => setShowAuthor(event.target.checked)}
                      />
                      <label className='form-check-label text-white font-size-13 font-weight-400'>
                        {formatMessage({ id: 'Show Author' })}
                      </label>
                      <p className='switch-text-color pt-1 font-size-12 font-weight-400'>
                        {formatMessage({ id: 'Add your name as author of the document' })}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Form */}
                <div className='information-form border-3 bg-white'>
                  <div className='pt-6'>
                    <span className='font-size-16 font-weight-600 pt-4'>
                      {formatMessage({ id: 'Information' })}
                    </span>
                  </div>
                  <div>
                    <div className='d-flex flex-column mb-5'>
                      <label className='form-label font-size-13 text-dark text-capitalize ms-3 ms-md-0'>
                        {formatMessage({ id: 'Update Title' })}*{' '}
                        <ToolTipUI
                          tooltipText={formatMessage({
                            id: 'GLOBAL.TOOLTIP.CREATEUPDATE.UPDATE_TITLE',
                          })}
                        />
                      </label>
                      <input
                        type='text'
                        className='form-control font-size-12 font-weight-400'
                        value={infoInput}
                        onChange={(event) => {
                          setInfoInput(event.target.value)
                          if (event.target.value.length >= 1 && event.target.value.length < 3) {
                            setInputError(formatMessage({ id: 'Minimum 3 characters' }))
                          } else {
                            setInputError(undefined)
                          }
                        }}
                        ref={inputRef}
                      />
                      <div className='mt-2'>
                        {inputError && (
                          <span className='text-danger font-size-13'>{inputError}</span>
                        )}
                      </div>
                    </div>
                    <div className='mt-4'>
                      <div className='d-flex'>
                        <input disabled type='text' className='copy-box' value={randomValue} />
                        <span className='copy-icon'>
                          <img
                            src={toAbsoluteUrl('/media/icons/investor-updates/copy.svg')}
                            alt=''
                            height='20px'
                            width='20px'
                            onClick={() => {
                              randomValue && navigator.clipboard.writeText(randomValue)
                              toast.success(formatMessage({ id: 'Copied Successfully' }))
                            }}
                          />
                        </span>
                        <div className='mt-1'>
                          <ToolTipUI
                            tooltipText={'GLOBAL.TOOLTIP.CREATEUPDATE.UPDATE_TITLE_RANDOM_VALUE'}
                          />
                        </div>
                      </div>
                      <div className='container mt-4 p-0 mb-4'>
                        <div className='pb-2'>
                          <label className='text-dark text-capitalize font-size-13 form-label'>
                            {formatMessage({ id: 'Update Timeframe' })}
                          </label>
                          <ToolTipUI tooltipText={'GLOBAL.TOOLTIP.DATEINPUT_UPDATE_TIMEFRAME'} />
                        </div>

                        <div className='date-container w-100 z-index-3'>
                          <DatePicker
                            className='form-control'
                            selected={startDate}
                            placeholderText={formatMessage({ id: 'Select timeframe' })}
                            onChange={(dates) => {
                              const [start, end] = dates
                              setStartDate(start)
                              setEndDate(end)
                              if (start !== null && end !== null) {
                                const dateStart = DateTime.fromJSDate(start)
                                const formattedDateStart = dateStart.toFormat('yyyy-MM-dd HH:mm:ss')
                                const dateEnd = DateTime.fromJSDate(end)
                                const formattedDateEnd = dateEnd.toFormat('yyyy-MM-dd HH:mm:ss')
                                const dateArray = [formattedDateStart, formattedDateEnd]
                                setInfoDate(JSON.stringify(dateArray))
                              }
                            }}
                            startDate={startDate}
                            endDate={endDate}
                            selectsRange
                            showPopperArrow={false}
                            showMonthDropdown
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <Droppable droppableId='main_drop_section'>
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      <div className='d-flex flex-column gap-5'>
                        {sectionData.map((section: any, index: number) => {
                          switch (section.type) {
                            case 'richText':
                              return (
                                <Draggable key={index} draggableId={index.toString()} index={index}>
                                  {(provided) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                    >
                                      <div className='d-flex flex-column gap-8 bg-white rounded p-8'>
                                        <div className='d-flex justify-content-between align-items-center w-100'>
                                          <div className='w-50'>
                                            <input
                                              type='text'
                                              className='form-control font-size-12 font-weight-400'
                                              placeholder='Add section title'
                                              value={section.inputText}
                                              onChange={(event) => {
                                                onInputTextValueChange(event.target.value, index)
                                              }}
                                            />
                                          </div>
                                          <p
                                            className='m-0 text-primary cursor-pointer'
                                            onClick={() => onDeleteWholeSection(index)}
                                          >
                                            {formatMessage({ id: 'Delete' })}
                                          </p>
                                        </div>

                                        <div className='d-flex flex-column gap-5'>
                                          <EditorComp
                                            index={index}
                                            onRichTextValueChange={onRichTextValueChange}
                                            richTextAreaValue={section.richText}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              )
                            case 'documents':
                              return (
                                <Draggable key={index} draggableId={index.toString()} index={index}>
                                  {(provided) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                    >
                                      <div className='d-flex flex-column gap-8 bg-white rounded p-8'>
                                        <div className='d-flex justify-content-between align-items-center w-100'>
                                          <div className='w-50'>
                                            <input
                                              type='text'
                                              className='form-control font-size-12 font-weight-400'
                                              placeholder='Add document title'
                                              value={section.inputText}
                                              onChange={(event) => {
                                                onInputTextValueChange(event.target.value, index)
                                              }}
                                            />
                                          </div>
                                          <p
                                            className='m-0 text-primary cursor-pointer'
                                            onClick={() => onDeleteWholeSection(index)}
                                          >
                                            {formatMessage({ id: 'Delete' })}
                                          </p>
                                        </div>
                                        <div className='dragfiles'>
                                          <DragDropFile
                                            onFileUploadCompleted={onFileUploadCompleted}
                                            fileUploaded={section.ids}
                                            index={index}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              )
                            case 'kpiValues':
                              return (
                                <Draggable key={index} draggableId={index.toString()} index={index}>
                                  {(provided) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                    >
                                      <div className='d-flex flex-column gap-8 bg-white rounded p-8'>
                                        <div className='d-flex justify-content-between align-items-center w-100'>
                                          <p className='font-size-16 m-0'>
                                            {formatMessage({ id: 'KPI' })}
                                          </p>
                                          <p
                                            className='text-primary m-0 cursor-pointer'
                                            onClick={() => onDeleteWholeSection(index)}
                                          >
                                            {formatMessage({ id: 'Delete' })}
                                          </p>
                                        </div>
                                        <div className='d-flex align-items-center gap-3'>
                                          <p className='m-0'>
                                            {formatMessage({
                                              id: `Choose KPIs which you want to display. Current currency is: `,
                                            })}
                                          </p>{' '}
                                          {currentUser?.company[0].currency?.code}
                                          {/* <select
                                            className={`form-select form-select-lg h-36px font-size-13 w-125px`}
                                            placeholder='select currency'
                                            value={section.currencyType}
                                            onChange={(e) => {
                                              kpiCurrencyChange(e.target.value, index)
                                            }}
                                          >
                                            {currencyOptions.map((currency) => (
                                              <option key={currency.id} value={currency.value}>
                                                {currency.name}
                                              </option>
                                            ))}
                                          </select> */}
                                        </div>
                                        <div className='d-flex flex-column gap-3'>
                                          <div className='d-flex flex-column gap-3'>
                                            <DragDropContext
                                              onDragEnd={(result: any) => onDragEnd(result, index)}
                                            >
                                              <Droppable droppableId='kpi_values_Drag'>
                                                {(provided) => (
                                                  <div
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                  >
                                                    {section?.kpiValues?.map(
                                                      (data: any, i: number) => (
                                                        <Draggable
                                                          key={data.id}
                                                          draggableId={data.id.toString()}
                                                          index={i}
                                                        >
                                                          {(provided) => (
                                                            <div
                                                              ref={provided.innerRef}
                                                              {...provided.draggableProps}
                                                              {...provided.dragHandleProps}
                                                            >
                                                              <KpiComponent
                                                                key={data.id}
                                                                data={data}
                                                                index={index}
                                                                handleToogle={handleToogle}
                                                                deleteKpi={deleteKpi}
                                                              />
                                                            </div>
                                                          )}
                                                        </Draggable>
                                                      )
                                                    )}
                                                    {provided.placeholder}
                                                  </div>
                                                )}
                                              </Droppable>
                                            </DragDropContext>
                                            <div className='kpiButtonContainer position-relative mt-2'>
                                              <img
                                                src={toAbsoluteUrl(
                                                  '/media/icons/investor-updates/plusButton.svg'
                                                )}
                                                alt='+'
                                                height='20px'
                                                width='20px'
                                                className='position-absolute kpiPlus z-index-1'
                                              />
                                              <button
                                                type='button'
                                                className={`btn custom-button-property kpiButton`}
                                                onClick={() =>
                                                  addNewKpi(section.kpiValues.length + 1, index)
                                                }
                                              >
                                                {formatMessage({ id: 'Add custom KPI' })}
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              )
                            case 'lineBreak':
                              return (
                                <Draggable key={index} draggableId={index.toString()} index={index}>
                                  {(provided) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                    >
                                      <div className='d-flex flex-column gap-8 bg-white rounded p-8'>
                                        <div className='d-flex justify-content-between align-items-center w-100'>
                                          <p className='font-size-14 m-0'>
                                            {formatMessage({ id: 'Line Break' })}
                                          </p>
                                          <p
                                            className='text-primary m-0 cursor-pointer'
                                            onClick={() => onDeleteWholeSection(index)}
                                          >
                                            {formatMessage({ id: 'Delete' })}
                                          </p>
                                        </div>
                                        <div className='border border-bottom-2 w-100'></div>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              )
                            default:
                              return null
                          }
                        })}
                        {provided.placeholder}
                      </div>
                    </div>
                  )}
                </Droppable>
                <div>
                  <form className='d-flex form-check-solid  form-switch gap-5 kpi-input'>
                    <input
                      className='form-check-input mt-1'
                      type='checkbox'
                      name='model.app.sidebar.default.minimize.desktop.hoverable'
                      checked={viewTracking}
                      onChange={(e) => setViewTracking(e.target.checked)}
                    />
                    <div>
                      <label className=' text-white font-size-13 font-weight-400'>
                        {formatMessage({ id: 'Track Views' })}
                      </label>
                      <p className='text-muted font-size-12 font-weight-400'>
                        {formatMessage({ id: 'Track visitors of this document' })}
                      </p>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </DragDropContext>
      </div>
      <DeleteConfirmationModal
        setDeleteShowModal={setDeleteShowModal}
        showDeleteModal={showDeleteModal}
        onDeleteSubmit={onDeleteSubmit}
        deleteLoading={deleteLoading}
      />
      <CopyConfirmationModal
        showMakeACopyModal={showMakeACopyModal}
        setMakeACopyShowModal={setMakeACopyShowModal}
        onCopySubmit={onCopySubmit}
        makeACopyLoading={makeACopyLoading}
        copyHeader={formatMessage({ id: 'Make Investor Update Copy' })}
        copyDiscription={formatMessage({
          id: 'Are you sure you want to make Investor Update Copy?',
        })}
      />
    </>
  )
}

export default CreateUpdates
